import { useEffect, useMemo, useRef } from "react";
import * as Sentry from "@sentry/nextjs";

interface ErrorWithExtras extends Error {
  digest?: string;
  correlationId?: string;
}

interface UseErrorPageOptions {
  error: ErrorWithExtras;
  /** Function to get the last correlation ID (from context or snapshot) */
  getCorrelationId: () => string | null;
}

interface UseErrorPageResult {
  /** The identifier to display (correlation ID, error digest, or generated client ID) */
  identifier: string;
  /** Label describing the identifier type */
  label: string;
  /** Error object formatted for display */
  displayError: Error;
}

/**
 * Generate a UUID for client-side errors with "client-" prefix
 * to distinguish from API correlation IDs
 */
export const generateClientErrorId = (): string => {
  return `client-${crypto.randomUUID()}`;
};

/**
 * Shared hook for error page logic.
 * Handles correlation ID resolution, Sentry reporting, and display formatting.
 */
export const useErrorPage = ({ error, getCorrelationId }: UseErrorPageOptions): UseErrorPageResult => {
  // Generate a stable client-side error ID if no other identifier is available
  // Use ref to keep it stable across re-renders
  const clientErrorIdRef = useRef<string | null>(null);
  if (!clientErrorIdRef.current) {
    clientErrorIdRef.current = generateClientErrorId();
  }

  const correlationId = error.correlationId ?? getCorrelationId() ?? error.digest ?? null;

  // Use client-generated ID as fallback when no server-side identifier exists
  const identifier = correlationId ?? clientErrorIdRef.current!;
  const label = correlationId ? "Correlation ID" : error.digest ? "Error ID" : "Client Error ID";

  // Report to Sentry with all available context
  useEffect(() => {
    Sentry.captureException(error, {
      tags: {
        ...(correlationId ? { correlationId } : {}),
        ...(error.digest ? { errorDigest: error.digest } : {}),
        // Always include the identifier for tracking
        errorIdentifier: identifier,
      },
      contexts: {
        correlation: {
          ...(correlationId ? { id: correlationId } : {}),
          ...(error.digest ? { digest: error.digest } : {}),
          identifier,
        },
        error: {
          message: error.message,
          name: error.name,
        },
      },
    });
  }, [error, correlationId, identifier]);

  // Create display error with identifier in message
  const displayError = useMemo(() => {
    const err = new Error(`${label}: ${identifier}`);
    err.stack = error.stack;
    return err;
  }, [identifier, label, error.stack]);

  return {
    identifier,
    label,
    displayError,
  };
};
